import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../../store';
import { verifyMagicLinkThunk, createSessionApiThunk } from '../api/authSlice';
import alert from '../../../utils/alert';
import useIsMobile from '../../../hooks/useIsMobile';
import { env } from '../../../env';

const isAndroid = (ua: string) => /android/i.test(ua);
const isIOS = (ua: string) => /iphone|ipad|ipod/i.test(ua);
const isMobileDevice = (ua: string) => isAndroid(ua) || isIOS(ua);

// Utility to try opening mobile app with fallback
const tryOpenMobileApp = async (code: string): Promise<boolean> => {
  const ua = navigator.userAgent || '';
  const isIOSDevice = isIOS(ua);
  const isAndroidDevice = isAndroid(ua);

  if (!isIOSDevice && !isAndroidDevice) {
    console.log('Not a mobile device, skipping app opening');
    return false;
  }

  // Use the domain from environment configuration
  const deepLinkDomain = env.DOMAIN_NAME || window.location.hostname;
  const deepLinkUrl = `https://${deepLinkDomain}/auth/login?code=${encodeURIComponent(code)}`;

  // Custom URL schemes as fallback
  const iosCustomScheme = `direct2care://auth/login?code=${encodeURIComponent(code)}`;
  const androidCustomScheme = `d2mdmobile://auth/login?code=${encodeURIComponent(code)}`;

  console.log('Attempting to open mobile app:', {
    isIOSDevice,
    isAndroidDevice,
    deepLinkDomain,
    deepLinkUrl,
    iosCustomScheme,
    androidCustomScheme,
  });

  //const originalVisibility = document.visibilityState;

  // Try to open the app using a more reliable approach
  return new Promise((resolve) => {
    const checkIfAppOpened = () => {
      if (document.visibilityState === 'hidden') {
        console.log('App opened successfully!');
        resolve(true);
        return true;
      }
      return false;
    };

    const handleVisibilityChange = () => {
      if (checkIfAppOpened()) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Try Universal Links/App Links first (most reliable)
    console.log('Trying Universal Links/App Links:', deepLinkUrl);
    window.location.href = deepLinkUrl;

    // Check if app opened after a short delay
    setTimeout(() => {
      if (!checkIfAppOpened()) {
        console.log('Universal Links failed, trying custom schemes');
        // If Universal Links didn't work, try custom schemes
        if (isIOSDevice) {
          console.log('Trying iOS custom scheme:', iosCustomScheme);
          window.location.href = iosCustomScheme;
        } else if (isAndroidDevice) {
          console.log('Trying Android custom scheme:', androidCustomScheme);
          window.location.href = androidCustomScheme;
        }

        // Final check
        setTimeout(() => {
          if (!checkIfAppOpened()) {
            console.log('All app opening attempts failed');
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            resolve(false);
          }
        }, 1500);
      }
    }, 1500);
  });
};

export const VerifyMagicLink: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const { t }: any = useTranslation();
  const isMobile = useIsMobile();

  const [isVerifying, setIsVerifying] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isOpeningApp, setIsOpeningApp] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  const hasRunRef = useRef(false);
  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const code = query.get('code') as string;

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const ua = navigator.userAgent || '';

    // Check if it's a mobile device (either by screen size or user agent)
    if (isMobile || isMobileDevice(ua)) {
      setIsOpeningApp(true);
      setIsVerifying(false);

      // Try to open the mobile app
      tryOpenMobileApp(code).then((appOpened) => {
        if (!appOpened) {
          // App didn't open, show fallback options
          setShowFallback(true);
          setIsOpeningApp(false);
        }
      });
      return; // skip web verification
    }

    const verify = async () => {
      if (!code) {
        alert.error(t('Invalid magic link'), { toastId: 'magiclink-invalid' });
        setHasError(true);
        setIsVerifying(false);
        return;
      }

      try {
        const res = await dispatch(verifyMagicLinkThunk({ code })).unwrap();

        console.log('response from verifyMagicLinkThunk:', res);
        if (res?.data?.statusCode === 200) {
          console.log('Verification success:', res);
          setIsVerifying(false);
          setHasError(false);
          // Create Session
          const cognitoAccessToken = res.data.resource?.AccessToken;
          await dispatch(
            createSessionApiThunk({
              accessToken: cognitoAccessToken || '',
            }),
          ).unwrap();
          //navigate('/dashboard');
        } else {
          throw new Error('Invalid or expired link');
        }
      } catch (error: any) {
        console.error('Verification failed:', error);
        alert.error(t(error.message || 'Something went wrong during verification.'), {
          toastId: 'magiclink-verification-failed',
        });
        setHasError(true);
        setIsVerifying(false);
      }
    };

    verify();
  }, [code, dispatch, t, isMobile]);

  const handleWebVerification = () => {
    setShowFallback(false);
    setIsVerifying(true);

    const verify = async () => {
      if (!code) {
        alert.error(t('Invalid magic link'), { toastId: 'magiclink-invalid' });
        setHasError(true);
        setIsVerifying(false);
        return;
      }

      try {
        const res = await dispatch(verifyMagicLinkThunk({ code })).unwrap();

        console.log('response from verifyMagicLinkThunk:', res);
        if (res?.data?.statusCode === 200) {
          console.log('Verification success:', res);
          setIsVerifying(false);
          setHasError(false);
          // Create Session
          const cognitoAccessToken = res.data.resource?.AccessToken;
          await dispatch(
            createSessionApiThunk({
              accessToken: cognitoAccessToken || '',
            }),
          ).unwrap();
          //navigate('/dashboard');
        } else {
          throw new Error('Invalid or expired link');
        }
      } catch (error: any) {
        console.error('Verification failed:', error);
        alert.error(t(error.message || 'Something went wrong during verification.'), {
          toastId: 'magiclink-verification-failed',
        });
        setHasError(true);
        setIsVerifying(false);
      }
    };

    verify();
  };

  const handleRetryApp = () => {
    setShowFallback(false);
    setIsOpeningApp(true);

    tryOpenMobileApp(code).then((appOpened) => {
      if (!appOpened) {
        setShowFallback(true);
        setIsOpeningApp(false);
      }
    });
  };

  return (
    <div className='d-flex flex-column align-items-center justify-content-center mt-5 text-center px-3'>
      {isOpeningApp ? (
        <div>
          <h2 className='auth__page-title'>{t('Opening App...')}</h2>
          <p className='auth__help-text'>{t('Please wait while we open the mobile app.')}</p>
        </div>
      ) : showFallback ? (
        <div>
          <h2 className='auth__page-title'>{t('App Not Found')}</h2>
          <p className='auth__help-text mb-4'>
            {t("We couldn't open the mobile app. You can either:")}
          </p>
          <div className='d-flex flex-column gap-3'>
            <button className='btn btn-primary' onClick={handleRetryApp}>
              {t('Try Opening App Again')}
            </button>
            <button className='btn btn-outline-primary' onClick={handleWebVerification}>
              {t('Continue in Browser')}
            </button>
          </div>
        </div>
      ) : isVerifying ? (
        <div>
          <h2 className='auth__page-title'>{t('Verifying...')}</h2>
          <p className='auth__help-text'>{t('Please wait while we verify your login link.')}</p>
        </div>
      ) : hasError ? (
        <div>
          <h2 className='auth__page-title text-danger'>{t('Verification failed')}</h2>
          <p className='auth__help-text'>
            {t('The login link is invalid or has expired. Please request a new one.')}
          </p>
        </div>
      ) : (
        <div>
          <h2 className='auth__page-title'>{t('Login successful')}</h2>
          <p className='auth__help-text'>
            {t('You have been successfully logged in. Redirecting...')}
          </p>
        </div>
      )}
    </div>
  );
};

export default VerifyMagicLink;
